import { ComponentType } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseFormStateReturn,
  useFormContext,
} from 'react-hook-form';
import type { FormField } from './types';

const WithController = <P extends AnyObject, F>(
  Component: ComponentType<P & ControlledComponentProps>,
) => {
  const NewComponent = <T extends FieldValues>(
    props: P & FormInputBaseProps<T, F>,
  ) => {
    const { trigger } = useFormContext<T>();

    return (
      <Controller
        name={props.name}
        render={({ field, fieldState, formState }) => {
          const triggerDependencies = () => {
            if (!props.dependencies) return;

            props.dependencies
              .filter(
                (dependency) => formState.dirtyFields[dependency] === true,
              )
              .forEach((dependency) => trigger(dependency));
          };

          const input = (
            <Component
              {...(props as P)}
              triggerDependencies={triggerDependencies}
              field={field}
              fieldState={fieldState}
              formState={formState}
            />
          );

          if (props.render) {
            return props.render({
              field: field as any,
              formState: formState as any,
              input,
              fieldState,
              triggerDependencies,
            });
          }

          return input;
        }}
      />
    );
  };

  return NewComponent;
};

export type ControlledComponentProps<
  T extends FieldValues = FieldValues,
  N = any,
> = {
  field: ControllerRenderProps<T, N extends FieldPath<T> ? N : never>;
  fieldState: ControllerFieldState;
  formState: UseFormStateReturn<T>;
  triggerDependencies: () => void;
};

type FormInputRender<T extends FieldValues = FieldValues, N = any> = (
  params: ControlledComponentProps<T, N> & { input: React.ReactElement },
) => React.ReactElement;

export type FormInputBaseProps<T extends FieldValues, F> = {
  render?: FormInputRender<T, FormField<T, F>>;
  name: FormField<T, F>;
  dependencies?: FieldPath<T>[];
};

export default WithController;
